import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Pill, Plus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RefillsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [refills, setRefills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    medication_name: '',
    dosage: '',
    quantity: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userRes, refillsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/refills`, { withCredentials: true })
      ]);
      setUser(userRes.data);
      setRefills(refillsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/refills`, formData, { withCredentials: true });
      toast.success('Refill requested successfully');
      setShowForm(false);
      setFormData({ medication_name: '', dosage: '', quantity: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to request refill');
    }
  };

  const handleApproval = async (id, status) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/refills/${id}`,
        { status },
        { withCredentials: true }
      );
      toast.success(`Refill ${status}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update refill');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary font-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="refills-page">
      {/* Header */}
      <div className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                data-testid="back-btn"
                onClick={() => navigate(user?.role === 'doctor' ? '/doctor-dashboard' : '/patient-dashboard')}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-3">
                <Pill className="w-8 h-8 text-accent" />
                <h1 className="font-heading text-xl font-semibold text-text-primary">Prescription Refills</h1>
              </div>
            </div>
            {user?.role === 'patient' && (
              <button
                data-testid="new-refill-btn"
                onClick={() => setShowForm(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Request Refill
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* New Refill Form */}
        {showForm && user?.role === 'patient' && (
          <div className="card-default mb-6" data-testid="refill-form">
            <h2 className="font-heading text-xl font-semibold text-text-primary mb-4">Request Refill</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-text-primary font-body font-medium mb-2">Medication Name</label>
                <input
                  data-testid="medication-input"
                  type="text"
                  value={formData.medication_name}
                  onChange={(e) => setFormData({ ...formData, medication_name: e.target.value })}
                  className="w-full bg-background-subtle border-transparent focus:border-primary focus:ring-1 focus:ring-primary rounded-lg h-12 px-4 transition-all"
                  placeholder="e.g., Lisinopril"
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-primary font-body font-medium mb-2">Dosage</label>
                  <input
                    data-testid="dosage-input"
                    type="text"
                    value={formData.dosage}
                    onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                    className="w-full bg-background-subtle border-transparent focus:border-primary focus:ring-1 focus:ring-primary rounded-lg h-12 px-4 transition-all"
                    placeholder="e.g., 10mg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-text-primary font-body font-medium mb-2">Quantity</label>
                  <input
                    data-testid="quantity-input"
                    type="text"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="w-full bg-background-subtle border-transparent focus:border-primary focus:ring-1 focus:ring-primary rounded-lg h-12 px-4 transition-all"
                    placeholder="e.g., 30 tablets"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="submit-refill-btn"
                  type="submit"
                  className="btn-primary"
                >
                  Submit Request
                </button>
                <button
                  data-testid="cancel-form-btn"
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Refills List */}
        <div className="space-y-4" data-testid="refills-list">
          {refills.length === 0 ? (
            <div className="card-default text-center py-12">
              <Pill className="w-16 h-16 text-text-muted mx-auto mb-4" />
              <p className="text-text-secondary font-body text-lg">No refill requests found</p>
              {user?.role === 'patient' && (
                <button
                  onClick={() => setShowForm(true)}
                  className="mt-4 btn-primary"
                >
                  Request Your First Refill
                </button>
              )}
            </div>
          ) : (
            refills.map((refill) => (
              <div key={refill.refill_id} className="card-default" data-testid={`refill-card-${refill.refill_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-body font-semibold text-text-primary text-lg">{refill.medication_name}</h3>
                    <p className="text-text-secondary font-body mt-1">
                      {refill.dosage} - {refill.quantity}
                    </p>
                    <p className="text-text-muted font-body text-sm mt-1">
                      Requested: {new Date(refill.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`status-badge status-${refill.status}`}>
                    {refill.status}
                  </span>
                </div>
                {refill.notes && (
                  <div className="bg-background-subtle rounded-lg p-3 mb-4">
                    <p className="text-text-secondary font-body text-sm"><strong>Notes:</strong> {refill.notes}</p>
                  </div>
                )}
                {user?.role === 'doctor' && refill.status === 'pending' && (
                  <div className="flex gap-3">
                    <button
                      data-testid={`approve-btn-${refill.refill_id}`}
                      onClick={() => handleApproval(refill.refill_id, 'approved')}
                      className="flex-1 bg-status-success text-white rounded-lg px-4 py-2 font-medium hover:opacity-90 transition-opacity"
                    >
                      Approve
                    </button>
                    <button
                      data-testid={`deny-btn-${refill.refill_id}`}
                      onClick={() => handleApproval(refill.refill_id, 'denied')}
                      className="flex-1 bg-status-error text-white rounded-lg px-4 py-2 font-medium hover:opacity-90 transition-opacity"
                    >
                      Deny
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}